import React, { FC, useState, useEffect, useRef } from "react";
import { Form, FormInstance, Input, Space, Typography, Button } from "antd";
import { InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { orgUnitCascaderFromValue } from "./create-proposal";
import {
  RelevantMatter,
  AccruedFee,
  MatterFilter,
  ExternalMatter,
  BaseAccruedFee,
} from "../../data-types";
import { FindRelevantMattersQuery, FindRelevantExternalMattersQuery } from "~/gql/graphql";
import { MatterTitleScreenTabKey, RelevantMatterTabs } from "./relevant-matter-tabs";
import { TitleInputState } from "./create-proposal";
import styles from "./create-proposal.css";

const {
  ["floating-input-container"]: floatingInputContainerClassName,
  ["floating-input"]: floatingInputClassName,
  ["floating-button"]: floatingButtonClassName,
  ["input-container"]: inputContainerClassName,
  ["title-hint"]: titleHintClassName,
  ["title-hint-text"]: titleHintTextClassName,
  ["main-content"]: mainContentClassName,
  ["warning-container"]: warningContainerClassName,
  ["workflow-guide-button"]: workflowGuideButtonClassName,
  spacer: spacerClassName,
} = styles;

type MatterTitleScreenProps = {
  form: FormInstance;
  matterTitle: string;
  additionalSearchText: string;
  setAdditionalSearchText: (value: string) => void;
  selectedMatter: RelevantMatter | null;
  setSelectedMatter: (value: RelevantMatter | null) => void;
  selectedAccruedFees: AccruedFee | null;
  selectedExternalMatter: ExternalMatter | null;
  setSelectedExternalMatter: (value: ExternalMatter | null) => void;
  selectedExternalAccruedFees: BaseAccruedFee | null;
  templateMatters: FindRelevantMattersQuery | undefined;
  historicalMatters: FindRelevantMattersQuery | undefined;
  historicalComparisonLoading: boolean;
  externalMatters: FindRelevantExternalMattersQuery | undefined;
  loading: boolean;
  templateMattersLoading: boolean;
  historicalMattersLoading: boolean;
  handleMatterTitleChange: (value: string) => void;
  activeTab: MatterTitleScreenTabKey;
  setActiveTab: (value: MatterTitleScreenTabKey) => void;
  isMatterTitleDescriptive: boolean | undefined;
  matterFilter: MatterFilter;
  handleFilterChange: (value: MatterFilter) => void;
  uiState: TitleInputState;
  setUiState: (state: TitleInputState) => void;
};

export const MatterTitleScreen: FC<MatterTitleScreenProps> = ({
  form,
  matterTitle,
  additionalSearchText,
  setAdditionalSearchText,
  selectedMatter,
  selectedAccruedFees,
  setSelectedMatter,
  selectedExternalMatter,
  setSelectedExternalMatter,
  selectedExternalAccruedFees,
  templateMatters,
  historicalMatters,
  historicalComparisonLoading,
  externalMatters,
  loading,
  templateMattersLoading,
  historicalMattersLoading,
  handleMatterTitleChange,
  activeTab,
  setActiveTab,
  isMatterTitleDescriptive,
  matterFilter,
  handleFilterChange,
  uiState,
  setUiState,
}) => {
  const [inputValue, setInputValue] = useState(matterTitle || "");

  const stateUpdatedRef = useRef(false);

  useEffect(() => {
    if (matterTitle === "") {
      setInputValue("");
      form.setFieldValue("title", "");
    } else if (matterTitle) {
      setInputValue(matterTitle);
      form.setFieldValue("title", matterTitle);
    }
  }, [matterTitle, form]);

  // Handle title confirmations when navigating between screens
  useEffect(() => {
    if (stateUpdatedRef.current) {
      stateUpdatedRef.current = false;
      return;
    }
    if (matterTitle && uiState === TitleInputState.CENTERED) {
      stateUpdatedRef.current = true;
      setUiState(TitleInputState.TOP_WITH_ANIMATION);
    } else if (!matterTitle && uiState === TitleInputState.TOP_WITH_ANIMATION) {
      stateUpdatedRef.current = true;
      setUiState(TitleInputState.CENTERED);
    }
  }, [matterTitle, uiState, setUiState]);

  const isInputCentered = () => uiState === TitleInputState.CENTERED;

  const shouldDisableTransitions = () => uiState === TitleInputState.TOP_WITHOUT_ANIMATION;

  const handleSelectedMatterChange = (matter: RelevantMatter | null) => {
    setSelectedMatter(matter);
    if (matter) {
      if (matter?.template) {
        form.setFieldValue("templateId", matter.id);
      } else {
        form.setFieldValue("historicalMatterId", matter.id);
      }
    } else {
      form.setFieldValue("templateId", null);
      form.setFieldValue("historicalMatterId", null);
    }
    if (matter?.organisationalUnit) {
      form.setFieldValue("practiceAreaId", orgUnitCascaderFromValue(matter.organisationalUnit));
    }
  };

  const confirmTitle = (value: string) => {
    if (value.trim()) {
      handleMatterTitleChange(value);
      stateUpdatedRef.current = true;
      setUiState(TitleInputState.TOP_WITH_ANIMATION);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      confirmTitle((e.target as HTMLInputElement).value);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    form.setFieldValue("title", value);

    if (!isInputCentered()) {
      handleMatterTitleChange(value);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        className={floatingInputContainerClassName}
        style={{
          transform: isInputCentered()
            ? "translate(calc(45vw - 50%), 35vh) scale(1.1)"
            : "translate(24px, -8px) scale(1)",
          transition: shouldDisableTransitions()
            ? "none"
            : "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Form.Item
          name="title"
          rules={[
            {
              required: true,
              message: "Please enter a title for your proposal",
            },
          ]}
          style={{ margin: 0 }}
        >
          <div className={inputContainerClassName}>
            <Input
              placeholder="Enter the title of your proposal"
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              className={floatingInputClassName}
              style={{
                fontSize: isInputCentered() ? "16px" : "14px",
                transition: shouldDisableTransitions()
                  ? "none"
                  : "font-size 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                padding: isInputCentered() ? "10px 16px" : undefined,
              }}
              value={inputValue}
            />
            {isInputCentered() && (
              <Button
                type="primary"
                onClick={() => confirmTitle(inputValue)}
                className={floatingButtonClassName}
                style={{
                  fontSize: isInputCentered() ? "16px" : "14px",
                  transition: shouldDisableTransitions()
                    ? "none"
                    : "font-size 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  padding: isInputCentered() ? "10px 20px" : undefined,
                }}
              >
                Confirm
              </Button>
            )}
          </div>
        </Form.Item>
        {isInputCentered() && (
          <div className={titleHintClassName}>
            <Typography.Text type="secondary">
              <div className={titleHintTextClassName}>
                Enter a title to see relevant proposal templates and historical matters. You can
                create a proposal from scratch, choose a <b>template</b>, or copy the scope and
                pricing details from a <b>historical matter</b>.
                <div style={{ marginTop: "8px", textAlign: "center" }}>
                  <a
                    href={`https://${window.location.host}/create?userpilot=ZXhwZXJpZW5jZTpyWlUyWDRxekdC`}
                    className={workflowGuideButtonClassName}
                  >
                    <QuestionCircleOutlined style={{ marginRight: "6px", fontSize: "12px" }} />
                    See the new workflow explained ✨
                  </a>
                </div>
              </div>
            </Typography.Text>
          </div>
        )}
      </div>

      <Form form={form} layout="vertical">
        <div className={spacerClassName} />

        <div
          className={mainContentClassName}
          style={{
            opacity: isInputCentered() ? 0 : 1,
            transform: `translateY(${isInputCentered() ? "20px" : "0"})`,
            transition: shouldDisableTransitions()
              ? "none"
              : "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            transitionDelay: isInputCentered() ? "0s" : "0.4s",
            visibility: isInputCentered() ? "hidden" : "visible",
            pointerEvents: isInputCentered() ? "none" : "auto",
          }}
        >
          {isMatterTitleDescriptive === false && (
            <Space align="start" className={warningContainerClassName}>
              <InfoCircleOutlined />
              <div>
                For more relevant suggestions, consider using a more descriptive title for your
                proposal (or enter more search terms in the search bar below).
              </div>
            </Space>
          )}

          {matterTitle && (
            <RelevantMatterTabs
              createMode={true}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              selectedMatter={selectedMatter}
              handleSelectedMatterChange={handleSelectedMatterChange}
              selectedAccruedFees={selectedAccruedFees}
              selectedExternalMatter={selectedExternalMatter}
              selectedExternalAccruedFees={selectedExternalAccruedFees}
              handleSelectedExternalMatterChange={setSelectedExternalMatter}
              templateMatters={templateMatters}
              historicalMatters={historicalMatters}
              historicalComparisonLoading={historicalComparisonLoading}
              externalMatters={externalMatters}
              additionalSearchText={additionalSearchText}
              setAdditionalSearchText={setAdditionalSearchText}
              templateMattersLoading={templateMattersLoading}
              historicalMattersLoading={historicalMattersLoading}
              maxHeight="65vh"
              matterFilter={matterFilter}
              handleFilterChange={handleFilterChange}
              allowChangeCurrency={true}
            />
          )}
        </div>
      </Form>
    </div>
  );
};
